import React from 'react';

/**
 * legalMarkdown — renders the small Markdown subset the legal documents in
 * docs/legal/*-draft.md are written in, as React elements (never raw HTML, so
 * nothing in a document can inject markup or script).
 *
 * Supported blocks: `#`/`##`/`###` headings, paragraphs, `>` callouts, `-`
 * and `1.` lists (one nested level via two-space indent), `---` rules.
 * Supported inline: `**bold**`, `*italic*`, `` `code` ``, `[text](url)`.
 * Links are limited to http(s), mailto, and same-site paths/anchors.
 */

export const slugify = (text) =>
  String(text)
    .toLowerCase()
    .replace(/[*_`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/)|#)/i;

const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

export const renderInline = (text, keyPrefix = 'i') => {
  const parts = String(text).split(INLINE).filter((part) => part !== '');
  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{renderInline(part.slice(2, -2), key)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={key}>{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const [, label, href] = link;
      if (!SAFE_HREF.test(href)) return <React.Fragment key={key}>{label}</React.Fragment>;
      const external = /^https?:\/\//i.test(href);
      return (
        <a
          key={key}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {renderInline(label, key)}
        </a>
      );
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{renderInline(part.slice(1, -1), key)}</em>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

/**
 * True for a `[Counsel note: …]` paragraph. The drafts carry these for the
 * reviewing attorney (internal file paths, open legal questions); the public
 * page leaves them out while docs/legal keeps them.
 *
 * @param {object} block - A parsed block.
 * @returns {boolean}
 */
export const isCounselNote = (block) => block.type === 'p' && /^\[Counsel note:/i.test(block.text);

const LIST_ITEM = /^(\s*)(-|\*|\d+\.)\s+(.*)$/;

/**
 * Parse Markdown into a flat list of block descriptors.
 *
 * @param {string} source - Markdown text.
 * @returns {Array<object>} Blocks: {type: 'h1'|'h2'|'h3'|'p'|'quote'|'hr'|'ul'|'ol', ...}.
 */
export const parseLegalMarkdown = (source) => {
  const lines = String(source).replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      blocks.push({ type: `h${level}`, text: heading[2].trim(), id: slugify(heading[2]) });
      i += 1;
      continue;
    }

    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('>')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      // Blank `>` lines split a callout into paragraphs.
      const paragraphs = quoteLines
        .join('\n')
        .split(/\n\s*\n/)
        .map((para) => para.split('\n').map((l) => l.trim()).join(' ').trim())
        .filter(Boolean);
      blocks.push({ type: 'quote', paragraphs });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items = [];
      while (i < lines.length) {
        const current = lines[i];
        const match = current.match(LIST_ITEM);
        if (match) {
          const nested = match[1].length >= 2;
          if (nested && items.length) {
            const parent = items[items.length - 1];
            parent.children = parent.children || [];
            parent.children.push(match[3].trim());
          } else if (!nested && /^\s*\d+\./.test(current) === ordered) {
            items.push({ text: match[3].trim() });
          } else {
            break;
          }
          i += 1;
        } else if (current.trim() && /^\s+/.test(current) && items.length) {
          // Indented continuation of the previous item.
          const parent = items[items.length - 1];
          if (parent.children && parent.children.length) {
            parent.children[parent.children.length - 1] += ` ${current.trim()}`;
          } else {
            parent.text += ` ${current.trim()}`;
          }
          i += 1;
        } else {
          break;
        }
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }

    const paraLines = [];
    while (
      i < lines.length
      && lines[i].trim()
      && !/^(#{1,3})\s/.test(lines[i].trim())
      && !lines[i].trim().startsWith('>')
      && !LIST_ITEM.test(lines[i])
      && !/^(-{3,}|\*{3,})$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'p', text: paraLines.join(' ') });
  }

  return blocks;
};

const renderList = (block, key, classNames) => {
  const Tag = block.type;
  return (
    <Tag key={key} className={classNames.list}>
      {block.items.map((item, index) => (
        <li key={`${key}-${index}`}>
          {renderInline(item.text, `${key}-${index}`)}
          {item.children && (
            <ul className={classNames.list}>
              {item.children.map((child, childIndex) => (
                <li key={`${key}-${index}-${childIndex}`}>
                  {renderInline(child, `${key}-${index}-${childIndex}`)}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </Tag>
  );
};

/**
 * Render parsed blocks to React elements.
 *
 * @param {Array<object>} blocks - Output of parseLegalMarkdown.
 * @param {object} [classNames] - Optional CSS-module class names keyed by block role.
 * @returns {Array<React.ReactElement>}
 */
export const renderLegalBlocks = (blocks, classNames = {}) =>
  blocks.map((block, index) => {
    const key = `b-${index}`;
    switch (block.type) {
      case 'h1':
        return <h1 key={key} id={block.id} className={classNames.h1}>{renderInline(block.text, key)}</h1>;
      case 'h2':
        return <h2 key={key} id={block.id} className={classNames.h2}>{renderInline(block.text, key)}</h2>;
      case 'h3':
        return <h3 key={key} id={block.id} className={classNames.h3}>{renderInline(block.text, key)}</h3>;
      case 'hr':
        return <hr key={key} className={classNames.hr} />;
      case 'quote':
        return (
          <aside key={key} className={classNames.quote} role="note">
            {block.paragraphs.map((para, pIndex) => (
              <p key={`${key}-${pIndex}`}>{renderInline(para, `${key}-${pIndex}`)}</p>
            ))}
          </aside>
        );
      case 'ul':
      case 'ol':
        return renderList(block, key, classNames);
      default:
        return <p key={key} className={classNames.p}>{renderInline(block.text, key)}</p>;
    }
  });
